import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import getConfig from 'next/config';

import {
    Box,
    Divider,
    Flex,
    IconButton,
    LabelTag,
    Link,
    Popover,
    Sheet,
} from '@iris/react-components';
import { XMarkIcon } from '@iris/react-icons/XMarkIcon';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { Button, Glyphicon, LoadingSpinner, Text } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import { aiAssistantEntryPointClick } from '@/analytics/aiAssistantEvents';

import activeInteractionDuck from '@/ducks/activeInteractionDuck.js';
import listingsAgentDuck from '@/ducks/listingsAgentDuck';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck';

import SearchInput from '@/components/SearchInput';

function ShoppingAssistantPopover({
    handleAssistantClose = () => { },
    headingText = 'What would you like to do?',
    show = false,
    suggestedActions = null,
    trigger,
    ...rest
}) {

    const dispatch = useDispatch();

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const messages = useSelector(listingsAgentDuck.selectors.getMessages);
    const isLoading = useSelector(listingsAgentDuck.selectors.isLoading);

    const vdpListing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const [searchQuery, setSearchQuery] = useState('');
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [buttonBottomPosition, setButtonBottomPosition] = useState(90);
    const [isProgrammaticScroll, setIsProgrammaticScroll] = useState(false);
    const [isUserScrolling, setIsUserScrolling] = useState(false);

    const chatContainerRef = useRef(null);
    const suggestedActionsRef = useRef(null);
    const lastPositionUpdateRef = useRef(0);
    const scrollTimeoutRef = useRef(null);

    // Helper to calculate button position above suggested actions
    const calculateButtonPosition = useCallback((containerRect, suggestedActionsRect) => {
        const suggestedActionsHeight = suggestedActionsRect.height;
        const distanceToActionsBottom = containerRect.bottom - suggestedActionsRect.bottom;
        const buttonHeight = 45;
        const gap = 60; // Gap for visual separation between button and suggested actions

        return distanceToActionsBottom + suggestedActionsHeight + buttonHeight + gap;
    }, []);

    // Helper to check if suggested actions are in viewport
    const areSuggestedActionsInView = useCallback((containerRect, suggestedActionsRect) => suggestedActionsRect.top < containerRect.bottom
        && suggestedActionsRect.bottom > containerRect.top, []);

    // Helper to determine if button position should be updated
    const shouldUpdateButtonPosition = useCallback((newPosition) => {
        const now = Date.now();
        const timeSinceLastUpdate = now - lastPositionUpdateRef.current;
        const isFirstRender = lastPositionUpdateRef.current === 0;
        const positionChanged = newPosition !== buttonBottomPosition;

        return positionChanged || isFirstRender || timeSinceLastUpdate > 33;
    }, [buttonBottomPosition]);

    // Helper to get button bottom position based on suggested actions visibility
    const getButtonBottomPosition = useCallback(() => {
        const defaultPosition = 90;

        if (!suggestedActionsRef.current || !chatContainerRef.current) {
            return defaultPosition;
        }

        const { scrollHeight, clientHeight } = chatContainerRef.current;
        const hasOverflow = scrollHeight > clientHeight;

        if (!hasOverflow) {
            return defaultPosition;
        }

        const containerRect = chatContainerRef.current.getBoundingClientRect();
        const suggestedActionsRect = suggestedActionsRef.current.getBoundingClientRect();

        if (areSuggestedActionsInView(containerRect, suggestedActionsRect)) {
            return calculateButtonPosition(containerRect, suggestedActionsRect);
        }

        return defaultPosition;
    }, [calculateButtonPosition, areSuggestedActionsInView]);

    // Check if content exceeds container height and user is not at bottom
    const checkContentOverflow = useCallback(() => {
        if (!chatContainerRef.current) {
            return;
        }

        const { scrollHeight, clientHeight, scrollTop } = chatContainerRef.current;
        const scrollBottom = scrollTop + clientHeight;
        const isAtBottom = Math.abs(scrollHeight - scrollBottom) <= 10; // 10px tolerance
        const hasOverflow = scrollHeight > clientHeight;

        // Update button position only when not in programmatic scroll
        if (!isProgrammaticScroll) {
            const newPosition = getButtonBottomPosition();

            if (shouldUpdateButtonPosition(newPosition)) {
                setButtonBottomPosition(newPosition);
                lastPositionUpdateRef.current = Date.now();
            }
        }

        setShowScrollButton(hasOverflow && !isAtBottom);
    }, [isProgrammaticScroll, getButtonBottomPosition, shouldUpdateButtonPosition]);

    // Check for overflow when new messages are added
    useEffect(() => {
        if (chatContainerRef.current && !isProgrammaticScroll) {
            checkContentOverflow();
        }
    }, [messages, isLoading, checkContentOverflow, isProgrammaticScroll]);

    // Check for overflow when suggested actions change or are rendered
    useEffect(() => {
        if (chatContainerRef.current && suggestedActions && !isProgrammaticScroll) {
            // Use a small delay to ensure suggested actions are fully rendered
            const timeoutId = setTimeout(() => {
                checkContentOverflow();
            }, 10);

            return () => clearTimeout(timeoutId);
        }

        return () => { }; // Return empty cleanup function when condition is false
    }, [suggestedActions, checkContentOverflow, isProgrammaticScroll]); // Memoized scroll handler with debouncing for smoother UX
    const handleScroll = useCallback(() => {
        // Don't process scrolls during programmatic scrolls (like scroll-to-bottom)
        if (isProgrammaticScroll) {
            return;
        }

        // Show that user is actively scrolling
        setIsUserScrolling(true);

        // Clear existing timeout
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        // Set timeout to check overflow after scrolling stops
        scrollTimeoutRef.current = setTimeout(() => {
            // Hide scrolling state and update position when scrolling stops
            setIsUserScrolling(false);
            checkContentOverflow();
        }, 100); // 100ms delay to detect scroll stop
    }, [checkContentOverflow, isProgrammaticScroll]);

    // Check overflow when component mounts or content changes
    useEffect(() => {
        if (!show) {
            // Return cleanup function even when show is false
            return () => { };
        }

        // Add resize observer to handle container size changes
        const resizeObserver = new ResizeObserver(() => {
            if (!isProgrammaticScroll) {
                checkContentOverflow();
            }
        });

        // Store the ref for cleanup - will be set in timeout
        let currentRefForCleanup = null;

        // Use a timeout to ensure DOM is ready and ref is available
        const timeoutId = setTimeout(() => {
            const currentRef = chatContainerRef.current;
            if (currentRef) {
                currentRefForCleanup = currentRef; // Store for cleanup
                // Check overflow after DOM is ready
                if (!isProgrammaticScroll) {
                    checkContentOverflow();
                }
                resizeObserver.observe(currentRef);
                // eslint-disable-next-line github/prefer-observers, listeners/no-missing-remove-event-listener
                currentRef.addEventListener('scroll', handleScroll);
            }
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            resizeObserver.disconnect();
            if (currentRefForCleanup) {
                currentRefForCleanup.removeEventListener('scroll', handleScroll);
            }
            // Clear scroll timeout on cleanup
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [checkContentOverflow, handleScroll, show, isProgrammaticScroll]);

    const { publicRuntimeConfig } = getConfig();

    const {
        ai_assistant_entry_point: [isAIAssistantEntryPointEnabled],
        suggested_keyword: [enableKeyword, { keyword_placeholder: keywordPlaceholder }],
        shopping_assistant: [, { listings_agent: enableListingsAgent }],
    } = useFeatures(['ai_assistant_entry_point', 'suggested_keyword', 'shopping_assistant']);

    const aiEndpoint = `${publicRuntimeConfig?.environment === 'prod' ? 'https://autotrader.com' : 'https://test.autotrader.com'}/ai-mode`;

    const onHide = useCallback(() => {
        handleAssistantClose();
    }, [handleAssistantClose]);

    const handleSearchInputChange = useCallback((event) => {
        setSearchQuery(event.target.value);
    }, []);

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();

        const userMessage = searchQuery.trim();

        // Only proceed if we have a non-empty message
        if (userMessage) {
            // Clear input immediately
            setSearchQuery('');

            await dispatch(listingsAgentDuck.creators.sendUserMessage(userMessage));
        }
    }, [searchQuery, dispatch]);

    const searchForm = (
        <form onSubmit={handleSubmit}>
            <SearchInput
                placeholder="Ask Anything"
                data-cmp="shopping-assistant-search"
                value={searchQuery}
                onChange={handleSearchInputChange}
                autoFocus
            />
        </form>
    );

    const keywordInput = enableKeyword && (
        <SearchInput
            placeholder={keywordPlaceholder}
            data-cmp="cntnr-kywrd"
            onFocus={() => {
                dispatch(activeInteractionDuck.creators.setKeys({
                    showKeywordSearchModal: true,
                }));
                handleAssistantClose();
            }}
            aria-label={keywordPlaceholder}
        />
    );

    const renderHtmlResponse = (htmlContent) => (
        <div className="iris:my-4 w-100">
            <Text size={200}>
                {
                    <div
                        className="shopping-assistant-response"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />
                }
            </Text>
        </div>
    );

    const renderMessage = (message) => {
        if (message.source === 'user') {
            return (
                <div
                    key={message.id}
                    className="iris:flex iris:justify-end w-100 iris:mb-3"
                >
                    <Box
                        className="iris:p-3 iris:bg-(--base-color-secondary-100) iris:rounded-3xl"
                    >
                        <Text size={200}>{message.content}</Text>
                    </Box>
                </div>
            );
        }

        return (
            <div key={message.id}>
                {renderHtmlResponse(message.content)}
            </div>
        );
    };

    const renderChatDialog = () => {
        const hasMessages = messages.length > 0;

        if (!hasMessages && !isLoading) {
            return null;
        }

        return (
            <>
                {messages.map(renderMessage)}

                {(isLoading) && (
                    <div className="iris:my-4 w-100">
                        <LoadingSpinner className="margin-top-1 margin-bottom-2" />
                    </div>
                )}
            </>
        );
    };

    const handleAiAssistantClick = (event) => {
        sendClick(aiAssistantEntryPointClick, event, {
            placement: 'shopping-assistant-popover',
            linkText: 'Ask Autotrader',
            url: aiEndpoint,
            cmp: 'text-link::try-our-new-AI-shopping-assistant',
            listingId: vdpListing?.id,
        });
    };

    const handleScrollToBottom = useCallback(() => {
        if (chatContainerRef.current) {
            // Set flag to prevent hiding button during programmatic scroll
            setIsProgrammaticScroll(true);

            // Scroll to the bottom of the container with smooth behavior
            chatContainerRef.current.scrollTo({
                top: chatContainerRef.current.scrollHeight,
                behavior: 'smooth',
            });

            // Wait for smooth scroll to complete, then hide button and reset flag
            setTimeout(() => {
                setIsProgrammaticScroll(false);
                // Check overflow state which will hide button if at bottom
                checkContentOverflow();
            }, 600);
        }
    }, [checkContentOverflow]);
    // Note:  replace with Iris component when it's available
    const floatingActionButton = showScrollButton && !isProgrammaticScroll && !isUserScrolling && (
        <Button
            bsSize="large"
            circle
            id="shopping-assistant-scroll-button"
            style={{
                bottom: buttonBottomPosition,
            }}
            onClick={handleScrollToBottom}
        >
            <Glyphicon
                className="text-size-500 text-accent margin-top-2"
                glyph="chevron-down"
            />
        </Button>
    );

    const content = (
        <div className="iris:flex iris:flex-col iris:grow gap-3">
            <div
                ref={chatContainerRef}
                data-cmp="shopping-assistant-chat-container"
                className="iris:flex iris:flex-wrap"
                style={{ maxHeight: 'calc(100vh - 330px)', overflow: 'hidden', overflowY: 'auto' }}
            >
                {floatingActionButton}
                {renderChatDialog()}

                {suggestedActions && (
                    <Flex
                        ref={suggestedActionsRef}
                        className="iris:mt-3 iris:w-95"
                        justify="start"
                        wrap="wrap"
                    >
                        {suggestedActions}
                    </Flex>
                )}

                {isAIAssistantEntryPointEnabled && (
                    <Flex className="iris:mt-3 iris:mx-1">
                        <Link
                            className="iris:mr-2"
                            href={aiEndpoint}
                            size="sm"
                            target="_blank"
                            weight="bold"
                            onClick={handleAiAssistantClick}
                        >
                            Let AI drive your car search. Ask Autotrader.
                        </Link>
                        <LabelTag text="BETA" />
                    </Flex>
                )}
            </div>
        </div>
    );

    if (isXs) {
        return (
            <div data-cmp="shopping-assistant-popover">
                <Sheet
                    desktopPosition="center"
                    fixedButtons
                    footerContent={enableListingsAgent ? searchForm : keywordInput}
                    hasHeader
                    modal={false}
                    onOpenChange={(open) => {
                        if (!open) {
                            onHide();
                        }
                    }}
                    open={show}
                    title={headingText}
                    trigger={trigger}
                    {...rest}
                >
                    {content}
                </Sheet>
            </div>
        );
    }

    // Desktop: Use Iris Popover component
    return (
        <Popover
            id="shopping-assistant-popover"
            data-cmp="shopping-assistant-popover"
            isOpen={show}
            trigger={trigger}
        >
            <div className="iris:flex iris:items-center iris:relative">
                <Text
                    className="iris:grow iris:text-center"
                    data-cmp="shopping-assist-dt-heading"
                    weight="bold"
                >
                    {headingText}
                </Text>
                <IconButton
                    className="iris:absolute"
                    style={{ right: 0 }}
                    onClick={handleAssistantClose}
                    icon={<XMarkIcon />}
                    size="sm"
                />
            </div>

            <Divider className="iris:my-3" />

            {content}

            <Divider className="iris:my-3" />

            {enableListingsAgent ? searchForm : keywordInput}
        </Popover>
    );
}

ShoppingAssistantPopover.propTypes = {
    /**
     * if true, the component will be shown
     * @default false
     */
    show: PropTypes.bool,
    /**
     * A group of CTA's to be rendered in the popover
     */
    suggestedActions: PropTypes.node,
    /**
     * The text to be displayed in the header of the popover
     */
    headingText: PropTypes.string,
    /**
     * Function to handle closing the assistant
     */
    handleAssistantClose: PropTypes.func,
    /**
     * Trigger element for the sheet
     */
    trigger: PropTypes.node,
};

export default React.memo(ShoppingAssistantPopover, _isEqual);
